/**
 * WaveformPlayer Component
 * Uses wavesurfer.js to display audio waveform with editable regions for subtitles
 */

import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import type { WaveSurferOptions } from 'wavesurfer.js';
import RegionsPlugin, { Region } from 'wavesurfer.js/dist/plugins/regions.js';
import ZoomPlugin from 'wavesurfer.js/dist/plugins/zoom.js';
import { getFullAudioUrl } from '../../utils/audioUrl';
import './WaveformPlayer.css';

// Constants
const PREVIEW_DURATION = 3; // seconds
const MIN_REGION_GAP = 0.05; // minimum gap between start and end in seconds
const AUTO_SCROLL_THRESHOLD = 40; // pixels from edge to trigger auto-scroll
const AUTO_SCROLL_SPEED = 3; // pixels per frame
const DEFAULT_ZOOM = 100; // default minPxPerSec
const ZOOM_STEP = 25;
const MIN_ZOOM = 50;
const MAX_ZOOM = 500;

interface Subtitle {
  index: number;
  text: string;
  startTime: number;
  endTime: number;
}

interface WaveformPlayerProps {
  audioUrl: string;
  subtitles: Subtitle[];
  selectedIndex: number | null;
  onRegionClick?: (index: number) => void;
  onRegionUpdate?: (index: number, start: number, end: number) => void;
  onTimeUpdate?: (time: number) => void;
  onReady?: () => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onSplitSubtitle?: (index: number, splitTime: number) => void;
  onError?: (error: Error) => void;
}

// Exported interface for ref handle
export interface WaveformPlayerHandle {
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  playSegment: (start: number, end: number) => void;
  setPlaybackRate: (rate: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  isPlaying: () => boolean;
  zoomIn: () => void;
  zoomOut: () => void;
}

const WaveformPlayer = forwardRef<WaveformPlayerHandle, WaveformPlayerProps>(
  ({ audioUrl, subtitles, selectedIndex, onRegionClick, onRegionUpdate, onTimeUpdate, onReady, onPlayStateChange, onSplitSubtitle, onError }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const regionsRef = useRef<RegionsPlugin | null>(null);
    const regionMapRef = useRef<Map<string, number>>(new Map());
    const isDestroyedRef = useRef(false);

    // Ref to store latest callbacks to avoid stale closures in event listeners
    const onTimeUpdateRef = useRef(onTimeUpdate);
    const onPlayStateChangeRef = useRef(onPlayStateChange);
    const onErrorRef = useRef(onError);
    const onReadyRef = useRef(onReady);

    // Ref to store latest subtitles to avoid stale closures
    const subtitlesRef = useRef(subtitles);
    subtitlesRef.current = subtitles;

    // Keep refs updated with latest callbacks
    useEffect(() => {
      onTimeUpdateRef.current = onTimeUpdate;
    }, [onTimeUpdate]);

    useEffect(() => {
      onPlayStateChangeRef.current = onPlayStateChange;
    }, [onPlayStateChange]);

    useEffect(() => {
      onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
      onReadyRef.current = onReady;
    }, [onReady]);

    // Split mode state (Command/Meta key held)
    const [isSplitMode, setIsSplitMode] = useState(false);
    const splitModeRef = useRef(false); // Ref for event handlers

    // Zoom state
    const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);

    // Preview playback state
    const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Track previous region positions to detect which handle was dragged
    const prevRegionPositionsRef = useRef<Map<number, { start: number; end: number }>>(new Map());

    // Track playSegment timeout for cleanup
    const playSegmentTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Track whether a region is being dragged/resized to prevent re-creating regions mid-drag
    const isDraggingRef = useRef(false);

    // Skip next createRegions call after drag-end (regions already in correct position)
    const skipNextCreateRef = useRef(false);

    // Auto-scroll animation frame ref
    const autoScrollRafRef = useRef<number | null>(null);

    // Flag to suppress AbortError during cleanup (React StrictMode causes double mount/unmount)
    const isCleaningUpRef = useRef(false);

    // Track Command/Meta key (macOS) or Ctrl key (Windows) for split mode
    // Only register keyboard listeners when split is available (onSplitSubtitle provided)
    useEffect(() => {
      if (!onSplitSubtitle) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        // Command key on macOS or Ctrl key on Windows
        if (e.metaKey || e.key === 'Meta' || e.ctrlKey || e.key === 'Control') {
          setIsSplitMode(true);
          splitModeRef.current = true;
        }
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        // Reset when either Command or Ctrl is released
        if (e.key === 'Meta' || e.key === 'Control' || (!e.metaKey && !e.ctrlKey)) {
          setIsSplitMode(false);
          splitModeRef.current = false;
        }
      };

      // Also handle blur to reset split mode when window loses focus
      const handleBlur = () => {
        setIsSplitMode(false);
        splitModeRef.current = false;
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      window.addEventListener('blur', handleBlur);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
        window.removeEventListener('blur', handleBlur);
      };
    }, [onSplitSubtitle]);

    // Stop any ongoing preview playback
    const stopPreview = useCallback(() => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      if (wavesurferRef.current?.isPlaying()) {
        wavesurferRef.current.pause();
      }
    }, []);

    // Format time as M:SS.cc (e.g., "1:23.45")
    const formatTime = useCallback((seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      const secsStr = secs < 10 ? `0${secs.toFixed(2)}` : secs.toFixed(2);
      return `${mins}:${secsStr}`;
    }, []);

    // Split mode toggle (for button-based split, works on both mobile and desktop)
    const toggleSplitMode = useCallback(() => {
      const newMode = !splitModeRef.current;
      setIsSplitMode(newMode);
      splitModeRef.current = newMode;
    }, []);

    // Zoom handlers
    const handleZoomIn = useCallback(() => {
      if (!wavesurferRef.current || isDestroyedRef.current) return;
      const newZoom = Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM);
      wavesurferRef.current.zoom(newZoom);
      setZoomLevel(newZoom);
    }, [zoomLevel]);

    const handleZoomOut = useCallback(() => {
      if (!wavesurferRef.current || isDestroyedRef.current) return;
      const newZoom = Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM);
      wavesurferRef.current.zoom(newZoom);
      setZoomLevel(newZoom);
    }, [zoomLevel]);

    // Check if device uses coarse pointer (touch)
    const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

    // Apply inline styles to a region handle (shadow DOM prevents CSS selectors)
    // Strategy: widen the handle bar itself into a "pill" shape as the grab target.
    // Separate thumb elements get clipped by wavesurfer's overflow:hidden wrapper.
    const styleHandle = useCallback((handle: HTMLElement, side: 'left' | 'right') => {
      // Bar width: thin line below the knob
      const barWidth = isTouchDevice ? 4 : 3;
      // Top "knob" section: wide for easy grabbing
      const knobWidth = isTouchDevice ? 28 : 18;
      const knobHeight = isTouchDevice ? 22 : 16;

      Object.assign(handle.style, {
        backgroundColor: 'transparent',
        width: `${knobWidth}px`,
        cursor: 'col-resize',
        zIndex: '10',
        position: 'absolute',
        top: '0',
        height: '100%',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      });
      // Position: center the wider handle on the region edge
      if (side === 'left') {
        handle.style.left = `-${Math.floor(knobWidth / 2)}px`;
      } else {
        handle.style.right = `-${Math.floor(knobWidth / 2)}px`;
      }

      // Clear default border styles set by wavesurfer
      handle.style.borderLeft = 'none';
      handle.style.borderRight = 'none';

      // Build the handle shape: knob (top) + bar (body)
      const existingKnob = handle.querySelector('.handle-knob');
      if (!existingKnob) {
        // Knob: rounded rectangle at top
        const knob = document.createElement('div');
        knob.className = 'handle-knob';
        Object.assign(knob.style, {
          width: `${knobWidth}px`,
          height: `${knobHeight}px`,
          backgroundColor: '#4285F4',
          borderRadius: '4px',
          flexShrink: '0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'background-color 0.2s ease, transform 0.15s ease',
          cursor: 'col-resize',
          // Grip lines indicator
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent ${isTouchDevice ? '4px' : '3px'},
            rgba(255,255,255,0.4) ${isTouchDevice ? '4px' : '3px'},
            rgba(255,255,255,0.4) ${isTouchDevice ? '6px' : '5px'}
          )`,
          backgroundPosition: 'center',
          backgroundSize: `${Math.floor(knobWidth * 0.6)}px 100%`,
          backgroundRepeat: 'no-repeat',
        });
        handle.appendChild(knob);

        // Bar: thin line extending below knob
        const bar = document.createElement('div');
        bar.className = 'handle-bar';
        Object.assign(bar.style, {
          width: `${barWidth}px`,
          flex: '1',
          backgroundColor: '#4285F4',
          borderRadius: '0 0 2px 2px',
          transition: 'background-color 0.2s ease',
          minHeight: '0',
        });
        handle.appendChild(bar);
      }

      // Hover effects
      handle.addEventListener('pointerenter', () => {
        const knob = handle.querySelector('.handle-knob') as HTMLElement | null;
        const bar = handle.querySelector('.handle-bar') as HTMLElement | null;
        if (knob) {
          knob.style.backgroundColor = '#357ae8';
          knob.style.transform = 'scaleX(1.1)';
        }
        if (bar) bar.style.backgroundColor = '#357ae8';
      });
      handle.addEventListener('pointerleave', () => {
        const knob = handle.querySelector('.handle-knob') as HTMLElement | null;
        const bar = handle.querySelector('.handle-bar') as HTMLElement | null;
        if (knob) {
          knob.style.backgroundColor = '#4285F4';
          knob.style.transform = 'scaleX(1)';
        }
        if (bar) bar.style.backgroundColor = '#4285F4';
      });
    }, [isTouchDevice]);

    // Add/update time label inside the knob element (avoids overflow clipping)
    const addTimeLabel = useCallback((handle: Element, time: number, _side: 'left' | 'right') => {
      const knob = handle.querySelector('.handle-knob') as HTMLElement | null;
      if (!knob) return;
      // Use knob's title attribute for tooltip, and show time in a label div
      knob.title = formatTime(time);

      let label = knob.querySelector('.handle-time-label') as HTMLElement | null;
      if (!label) {
        label = document.createElement('div');
        label.className = 'handle-time-label';
        Object.assign(label.style, {
          position: 'absolute',
          top: '-16px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '9px',
          fontWeight: '600',
          color: '#4285F4',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '1px 4px',
          borderRadius: '3px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          opacity: '0',
          transition: 'opacity 0.15s ease',
          lineHeight: '1.2',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          zIndex: '20',
        });
        knob.style.position = 'relative';
        knob.style.overflow = 'visible';
        knob.appendChild(label);
      }
      label.textContent = formatTime(time);
    }, [formatTime]);

    // Update time labels on region handles
    const updateHandleTimeLabels = useCallback((region: Region) => {
      if (!region.element) return;
      const leftHandle = region.element.querySelector('[part*="region-handle-left"]') as HTMLElement | null;
      const rightHandle = region.element.querySelector('[part*="region-handle-right"]') as HTMLElement | null;
      if (leftHandle) addTimeLabel(leftHandle, region.start, 'left');
      if (rightHandle) addTimeLabel(rightHandle, region.end, 'right');
    }, [addTimeLabel]);

    // Style and setup handles for a region
    const setupRegionHandles = useCallback((region: Region) => {
      if (!region.element) return;
      const leftHandle = region.element.querySelector('[part*="region-handle-left"]') as HTMLElement | null;
      const rightHandle = region.element.querySelector('[part*="region-handle-right"]') as HTMLElement | null;

      const setupSingleHandle = (handle: HTMLElement, side: 'left' | 'right') => {
        styleHandle(handle, side);
        // Show/hide time label on hover (desktop only)
        if (!isTouchDevice) {
          handle.addEventListener('pointerenter', () => {
            const label = handle.querySelector('.handle-time-label') as HTMLElement | null;
            if (label) label.style.opacity = '1';
          });
          handle.addEventListener('pointerleave', () => {
            const label = handle.querySelector('.handle-time-label') as HTMLElement | null;
            if (label) label.style.opacity = '0';
          });
        } else {
          // On touch: always show labels
          setTimeout(() => {
            const label = handle.querySelector('.handle-time-label') as HTMLElement | null;
            if (label) label.style.opacity = '1';
          }, 0);
        }
      };

      if (leftHandle) setupSingleHandle(leftHandle, 'left');
      if (rightHandle) setupSingleHandle(rightHandle, 'right');
    }, [styleHandle, isTouchDevice]);

    // Toggle a region's handles between selected (visible, draggable) and
    // unselected (hidden knob, no resize cursor). Keeps adjacent-sentence
    // handles from being grabbed when the user is focused on one sentence.
    const applyHandleSelectedState = useCallback((region: Region, isSelected: boolean) => {
      if (!region.element) return;
      const handles = [
        region.element.querySelector('[part*="region-handle-left"]') as HTMLElement | null,
        region.element.querySelector('[part*="region-handle-right"]') as HTMLElement | null,
      ];
      for (const handle of handles) {
        if (!handle) continue;
        handle.style.cursor = isSelected ? 'col-resize' : 'default';
        handle.style.pointerEvents = isSelected ? 'auto' : 'none';
        const knob = handle.querySelector('.handle-knob') as HTMLElement | null;
        const bar = handle.querySelector('.handle-bar') as HTMLElement | null;
        if (knob) knob.style.display = isSelected ? 'flex' : 'none';
        if (bar) bar.style.display = isSelected ? 'block' : 'none';
      }
    }, []);

    // Auto-scroll when dragging near edges
    const startAutoScroll = useCallback((region: Region, side: string) => {
      const stopAutoScroll = () => {
        if (autoScrollRafRef.current) {
          cancelAnimationFrame(autoScrollRafRef.current);
          autoScrollRafRef.current = null;
        }
      };

      stopAutoScroll();

      const tick = () => {
        if (!wavesurferRef.current || isDestroyedRef.current || !region.element) {
          stopAutoScroll();
          return;
        }

        const wrapper = wavesurferRef.current.getWrapper();
        const scrollContainer = wrapper?.parentElement;
        if (!scrollContainer) {
          stopAutoScroll();
          return;
        }

        const containerRect = scrollContainer.getBoundingClientRect();
        const handle = side === 'start'
          ? region.element.querySelector('[part*="region-handle-left"]')
          : region.element.querySelector('[part*="region-handle-right"]');

        if (!handle) {
          stopAutoScroll();
          return;
        }

        const handleRect = handle.getBoundingClientRect();
        const handleCenter = handleRect.left + handleRect.width / 2;
        const distFromLeft = handleCenter - containerRect.left;
        const distFromRight = containerRect.right - handleCenter;

        if (distFromLeft < AUTO_SCROLL_THRESHOLD) {
          scrollContainer.scrollLeft -= AUTO_SCROLL_SPEED;
        } else if (distFromRight < AUTO_SCROLL_THRESHOLD) {
          scrollContainer.scrollLeft += AUTO_SCROLL_SPEED;
        }

        autoScrollRafRef.current = requestAnimationFrame(tick);
      };

      autoScrollRafRef.current = requestAnimationFrame(tick);
      return stopAutoScroll;
    }, []);

    // Create regions from subtitles
    const createRegions = useCallback(() => {
      if (!regionsRef.current || !wavesurferRef.current || isDestroyedRef.current) return;

      // Clear existing regions
      regionsRef.current.clearRegions();
      regionMapRef.current.clear();
      prevRegionPositionsRef.current.clear();

      // Use ref to get the latest subtitles (avoids stale closure issues)
      const currentSubtitles = subtitlesRef.current;

      // Create region for each subtitle
      currentSubtitles.forEach((subtitle) => {
        if (!regionsRef.current) return;

        const isSelected = subtitle.index === selectedIndex;
        const region = regionsRef.current.addRegion({
          start: subtitle.startTime,
          end: subtitle.endTime,
          color: isSelected ? 'rgba(66, 133, 244, 0.3)' : 'rgba(0, 0, 0, 0.1)',
          drag: false, // Disable dragging the entire region
          resize: isSelected, // Only the focused sentence exposes resize handles
        });

        // Store mapping
        regionMapRef.current.set(region.id, subtitle.index);

        // Store initial position for preview detection
        prevRegionPositionsRef.current.set(subtitle.index, {
          start: subtitle.startTime,
          end: subtitle.endTime,
        });

        // Style handles and add time labels (inline styles needed for shadow DOM)
        setupRegionHandles(region);
        updateHandleTimeLabels(region);
        applyHandleSelectedState(region, isSelected);

        // Track auto-scroll stop function
        let stopAutoScrollFn: (() => void) | null = null;

        // Handle region update (during drag) - enforce min gap + neighbor clamps
        region.on('update', (side?: string) => {
          if (isDestroyedRef.current) return;
          isDraggingRef.current = true;
          stopPreview();

          // Resolve immediate neighbors (sorted by startTime, excluding self).
          // Using sort instead of subtitle.index so deletes/splits stay safe.
          const allSubs = subtitlesRef.current;
          const sorted = allSubs.slice().sort((a, b) => a.startTime - b.startTime);
          const myPos = sorted.findIndex((s) => s.index === subtitle.index);
          const prev = myPos > 0 ? sorted[myPos - 1] : null;
          const next = myPos >= 0 && myPos < sorted.length - 1 ? sorted[myPos + 1] : null;

          // Clamp the dragged handle:
          //   - own start ↔ own end gap stays ≥ MIN_REGION_GAP
          //   - own start cannot cross prev.endTime − MIN_REGION_GAP into the previous sentence
          //   - own end cannot cross next.startTime − MIN_REGION_GAP into the next sentence
          if (side === 'start') {
            const lowerBound = prev ? prev.endTime + MIN_REGION_GAP : 0;
            const upperBound = region.end - MIN_REGION_GAP;
            if (region.start < lowerBound) {
              region.setOptions({ start: lowerBound });
            } else if (region.start > upperBound) {
              region.setOptions({ start: upperBound });
            }
          } else if (side === 'end') {
            const lowerBound = region.start + MIN_REGION_GAP;
            const upperBound = next
              ? next.startTime - MIN_REGION_GAP
              : (wavesurferRef.current?.getDuration() ?? region.end);
            if (region.end > upperBound) {
              region.setOptions({ end: upperBound });
            } else if (region.end < lowerBound) {
              region.setOptions({ end: lowerBound });
            }
          }

          // Update time labels during drag and force them visible
          updateHandleTimeLabels(region);
          if (!isTouchDevice && region.element) {
            region.element.querySelectorAll('.handle-time-label').forEach((el) => {
              (el as HTMLElement).style.opacity = '1';
            });
          }

          // Start auto-scroll if not already running
          if (side && !stopAutoScrollFn) {
            stopAutoScrollFn = startAutoScroll(region, side) || null;
          }
        });

        // Handle region click using WaveSurfer's built-in event system
        region.on('click', (e: Event) => {
          if (isDestroyedRef.current) return;
          // Prevent click from propagating to wavesurfer
          e.stopPropagation();

          const mouseEvent = e as MouseEvent;

          // Check if in split mode (Command key held OR toggle button active)
          if (splitModeRef.current && onSplitSubtitle && wavesurferRef.current && region.element) {
            // Only allow split on the currently selected region
            if (subtitle.index !== selectedIndex) {
              // Clicking a non-selected region in split mode: just select it instead
              if (onRegionClick) onRegionClick(subtitle.index);
              return;
            }

            // Calculate click time based on mouse/touch position
            const rect = region.element.getBoundingClientRect();
            const touchEvent = mouseEvent as unknown as TouchEvent;
            const clientX = touchEvent.touches?.length
              ? touchEvent.touches[0].clientX
              : mouseEvent.clientX;
            const clickX = clientX - rect.left;
            const regionWidth = rect.width;
            const regionDuration = region.end - region.start;
            const clickTime = region.start + (clickX / regionWidth) * regionDuration;

            // Only split if click is not too close to edges (at least 0.3s from each edge)
            const minSplitDistance = 0.3;
            if (clickTime > region.start + minSplitDistance && clickTime < region.end - minSplitDistance) {
              onSplitSubtitle(subtitle.index, clickTime);
              // Exit split mode after successful split
              setIsSplitMode(false);
              splitModeRef.current = false;
              return;
            }
          }

          if (onRegionClick) {
            onRegionClick(subtitle.index);
          }
          // Seek to region start
          if (wavesurferRef.current) {
            wavesurferRef.current.seekTo(region.start / wavesurferRef.current.getDuration());
          }
        });

        // Handle region update-end (when user finishes resizing)
        region.on('update-end', () => {
          if (isDestroyedRef.current) return;
          isDraggingRef.current = false;

          // Stop auto-scroll
          if (stopAutoScrollFn) {
            stopAutoScrollFn();
            stopAutoScrollFn = null;
          }
          if (autoScrollRafRef.current) {
            cancelAnimationFrame(autoScrollRafRef.current);
            autoScrollRafRef.current = null;
          }

          // Update time labels after drag ends
          updateHandleTimeLabels(region);

          const prevPos = prevRegionPositionsRef.current.get(subtitle.index);
          const newStart = region.start;
          const newEnd = region.end;

          // Detect which handle was dragged and play preview
          if (prevPos && wavesurferRef.current) {
            const duration = wavesurferRef.current.getDuration();
            const startChanged = Math.abs(newStart - prevPos.start) > 0.01;
            const endChanged = Math.abs(newEnd - prevPos.end) > 0.01;

            if (startChanged && !endChanged) {
              // Start handle was dragged - play from start to start + PREVIEW_DURATION
              const previewEnd = Math.min(newStart + PREVIEW_DURATION, newEnd);
              wavesurferRef.current.seekTo(newStart / duration);
              wavesurferRef.current.play();

              // Set timeout to stop after preview duration
              const previewDuration = (previewEnd - newStart) * 1000;
              previewTimeoutRef.current = setTimeout(() => {
                if (!isDestroyedRef.current && wavesurferRef.current?.isPlaying()) {
                  wavesurferRef.current.pause();
                }
                previewTimeoutRef.current = null;
              }, previewDuration);
            } else if (endChanged && !startChanged) {
              // End handle was dragged - play from end - PREVIEW_DURATION to end
              const previewStart = Math.max(newEnd - PREVIEW_DURATION, newStart);
              wavesurferRef.current.seekTo(previewStart / duration);
              wavesurferRef.current.play();

              // Set timeout to stop after preview duration
              const previewDuration = (newEnd - previewStart) * 1000;
              previewTimeoutRef.current = setTimeout(() => {
                if (!isDestroyedRef.current && wavesurferRef.current?.isPlaying()) {
                  wavesurferRef.current.pause();
                }
                previewTimeoutRef.current = null;
              }, previewDuration);
            }

            // Update stored position
            prevRegionPositionsRef.current.set(subtitle.index, {
              start: newStart,
              end: newEnd,
            });
          }

          if (onRegionUpdate) {
            // Skip next createRegions since regions are already at correct positions
            skipNextCreateRef.current = true;
            onRegionUpdate(subtitle.index, newStart, newEnd);
          }
        });
      });
    }, [selectedIndex, onRegionClick, onRegionUpdate, onSplitSubtitle, stopPreview, setupRegionHandles, updateHandleTimeLabels, applyHandleSelectedState, startAutoScroll, isTouchDevice]);

    // Initialize wavesurfer
    useEffect(() => {
      if (!containerRef.current || !audioUrl) return;

      isDestroyedRef.current = false;

      // Register AbortError suppression handler (must be in same effect as destroy)
      // This handles the async AbortError thrown by WaveSurfer when destroyed during loading
      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        if (
          isCleaningUpRef.current &&
          (event.reason?.name === 'AbortError' ||
            event.reason?.message?.includes('signal is aborted'))
        ) {
          event.preventDefault();
        }
      };
      window.addEventListener('unhandledrejection', handleUnhandledRejection);

      // WaveSurfer options with proper typing
      const options: WaveSurferOptions = {
        container: containerRef.current,
        waveColor: '#ddd',
        progressColor: '#4285F4',
        cursorColor: '#ff6b6b',
        barWidth: 2,
        barRadius: 3,
        cursorWidth: 1,
        height: 60,
        barGap: 1,
        normalize: true,
        minPxPerSec: 100,
        autoCenter: true,
        autoScroll: true,
        hideScrollbar: true,
      };

      // Create wavesurfer instance
      const ws = WaveSurfer.create(options);

      // Initialize regions plugin
      const regions = ws.registerPlugin(RegionsPlugin.create());

      // Initialize zoom plugin (supports pinch-to-zoom on touch devices)
      ws.registerPlugin(ZoomPlugin.create({
        scale: 0.5,
        maxZoom: MAX_ZOOM,
        deltaThreshold: 5,
      }));

      // Sync zoom level state when zoom changes (from pinch or scroll).
      // Wheel-zoom in ZoomPlugin can clamp newZoom down to container/duration
      // so the waveform exactly fits the viewport (no horizontal overflow,
      // overflow-x: hidden) — at which point the user can no longer pan.
      // Snap back to MIN_ZOOM so horizontal scroll is always available.
      ws.on('zoom', (newZoom: number) => {
        if (isDestroyedRef.current) return;
        if (newZoom < MIN_ZOOM) {
          ws.zoom(MIN_ZOOM);
          setZoomLevel(MIN_ZOOM);
          return;
        }
        setZoomLevel(Math.round(newZoom));
      });

      wavesurferRef.current = ws;
      regionsRef.current = regions;

      // Load audio with full URL - catch AbortError that occurs when destroyed during loading
      ws.load(getFullAudioUrl(audioUrl)).catch((err) => {
        // Suppress AbortError (happens in React StrictMode when component unmounts during load)
        if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
          return;
        }
        console.error('[WaveformPlayer] Load error:', err);
      });

      // Event handlers
      ws.on('ready', () => {
        if (isDestroyedRef.current) return;

        createRegions();
        if (onReadyRef.current) onReadyRef.current();
      });

      // 'timeupdate' fires reliably during playback (more consistent than 'audioprocess')
      ws.on('timeupdate', () => {
        if (isDestroyedRef.current) return;
        if (onTimeUpdateRef.current) {
          onTimeUpdateRef.current(ws.getCurrentTime());
        }
      });

      ws.on('audioprocess', () => {
        if (isDestroyedRef.current) return;
        if (onTimeUpdateRef.current) {
          onTimeUpdateRef.current(ws.getCurrentTime());
        }
      });

      ws.on('seeking', () => {
        if (isDestroyedRef.current) return;
        if (onTimeUpdateRef.current) {
          onTimeUpdateRef.current(ws.getCurrentTime());
        }
      });

      ws.on('interaction', () => {
        if (isDestroyedRef.current) return;
        if (onTimeUpdateRef.current) {
          onTimeUpdateRef.current(ws.getCurrentTime());
        }
      });

      // Play/pause state change events
      ws.on('play', () => {
        if (isDestroyedRef.current) return;
        if (onPlayStateChangeRef.current) {
          onPlayStateChangeRef.current(true);
        }
      });

      ws.on('pause', () => {
        if (isDestroyedRef.current) return;
        if (onPlayStateChangeRef.current) {
          onPlayStateChangeRef.current(false);
        }
      });

      ws.on('finish', () => {
        if (isDestroyedRef.current) return;
        if (onPlayStateChangeRef.current) {
          onPlayStateChangeRef.current(false);
        }
      });

      // Error handling
      ws.on('error', (error) => {
        if (isDestroyedRef.current) return;
        // Suppress AbortError (happens in React StrictMode when component unmounts during load)
        if (error?.name === 'AbortError' || (error instanceof Error && error.message?.includes('aborted'))) {
          return;
        }
        console.error('[WaveformPlayer] Error loading audio:', error);
        if (onErrorRef.current) {
          onErrorRef.current(error instanceof Error ? error : new Error(String(error)));
        }
      });

      // Cleanup - properly destroy WaveSurfer
      return () => {
        isDestroyedRef.current = true;

        // Clear all timeouts and animation frames
        if (previewTimeoutRef.current) {
          clearTimeout(previewTimeoutRef.current);
          previewTimeoutRef.current = null;
        }
        if (playSegmentTimeoutRef.current) {
          clearTimeout(playSegmentTimeoutRef.current);
          playSegmentTimeoutRef.current = null;
        }
        if (autoScrollRafRef.current) {
          cancelAnimationFrame(autoScrollRafRef.current);
          autoScrollRafRef.current = null;
        }

        // Clear refs
        wavesurferRef.current = null;
        regionsRef.current = null;

        // Remove unhandledrejection listener
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);

        // Destroy WaveSurfer - AbortError from load() is already caught by .catch()
        // Any synchronous error from destroy() is caught here
        try {
          ws.destroy();
        } catch {
          // Suppress errors during destroy (expected in React StrictMode)
        }
      };
    // Only re-initialize when audioUrl changes
    // createRegions is called in 'ready' event and in separate effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

    // Create/update regions when subtitles change (skip during drag to prevent visual jump)
    useEffect(() => {
      if (!wavesurferRef.current || !regionsRef.current || isDestroyedRef.current) return;
      if (isDraggingRef.current) return;
      // Skip recreation after drag-end since regions are already positioned correctly
      if (skipNextCreateRef.current) {
        skipNextCreateRef.current = false;
        return;
      }
      createRegions();
    }, [subtitles, createRegions]);

    // Update selected region appearance + resize gating
    useEffect(() => {
      if (!regionsRef.current || isDestroyedRef.current) return;

      const regions = regionsRef.current.getRegions();
      regions.forEach((region: Region) => {
        const index = regionMapRef.current.get(region.id);
        if (index !== undefined) {
          const isSelected = index === selectedIndex;
          region.setOptions({
            color: isSelected ? 'rgba(66, 133, 244, 0.3)' : 'rgba(0, 0, 0, 0.1)',
            resize: isSelected,
          });
          applyHandleSelectedState(region, isSelected);
        }
      });
    }, [selectedIndex, applyHandleSelectedState]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      play: () => {
        if (!isDestroyedRef.current) {
          stopPreview(); // Stop any ongoing preview
          wavesurferRef.current?.play();
        }
      },
      pause: () => {
        if (!isDestroyedRef.current) {
          stopPreview(); // Clear preview timeout
          wavesurferRef.current?.pause();
        }
      },
      seekTo: (time: number) => {
        if (isDestroyedRef.current || !wavesurferRef.current) return;
        stopPreview(); // Stop any ongoing preview
        const duration = wavesurferRef.current.getDuration();
        if (duration > 0) {
          wavesurferRef.current.seekTo(time / duration);
        }
      },
      playSegment: (start: number, end: number) => {
        if (isDestroyedRef.current || !wavesurferRef.current) return;
        stopPreview(); // Stop any ongoing preview

        // Clear any previous playSegment timeout
        if (playSegmentTimeoutRef.current) {
          clearTimeout(playSegmentTimeoutRef.current);
          playSegmentTimeoutRef.current = null;
        }

        const duration = wavesurferRef.current.getDuration();
        if (duration > 0) {
          wavesurferRef.current.seekTo(start / duration);
          wavesurferRef.current.play();

          // Set timeout to pause at end (tracked for cleanup)
          const timeUntilEnd = (end - start) * 1000;
          playSegmentTimeoutRef.current = setTimeout(() => {
            if (!isDestroyedRef.current && wavesurferRef.current?.isPlaying()) {
              const currentTime = wavesurferRef.current.getCurrentTime();
              if (currentTime >= end - 0.1) {
                wavesurferRef.current.pause();
              }
            }
            playSegmentTimeoutRef.current = null;
          }, timeUntilEnd);
        }
      },
      setPlaybackRate: (rate: number) => {
        if (!isDestroyedRef.current) wavesurferRef.current?.setPlaybackRate(rate);
      },
      getCurrentTime: () => {
        return wavesurferRef.current?.getCurrentTime() || 0;
      },
      getDuration: () => {
        return wavesurferRef.current?.getDuration() || 0;
      },
      isPlaying: () => {
        return wavesurferRef.current?.isPlaying() || false;
      },
      zoomIn: () => {
        if (!isDestroyedRef.current) handleZoomIn();
      },
      zoomOut: () => {
        if (!isDestroyedRef.current) handleZoomOut();
      },
    }), [stopPreview, handleZoomIn, handleZoomOut]);

    return (
      <div
        className={`waveform-player ${isSplitMode ? 'split-mode' : ''}`}
        role="application"
        aria-label="Audio waveform editor"
      >
        <div className="waveform-container-wrapper">
          <div ref={containerRef} className="waveform-container" />
          <div className="waveform-zoom-controls">
            {onSplitSubtitle && (
              <button
                className={`waveform-tool-btn waveform-split-btn ${isSplitMode ? 'active' : ''}`}
                onClick={toggleSplitMode}
                aria-label={isSplitMode ? 'Exit split mode' : 'Enter split mode'}
                title={isSplitMode ? 'Exit split mode' : 'Split subtitle'}
              >
                ✂
              </button>
            )}
            <button
              className="waveform-tool-btn waveform-zoom-btn"
              onClick={handleZoomOut}
              disabled={zoomLevel <= MIN_ZOOM}
              aria-label="Zoom out"
              title="Zoom out"
            >
              −
            </button>
            <button
              className="waveform-tool-btn waveform-zoom-btn"
              onClick={handleZoomIn}
              disabled={zoomLevel >= MAX_ZOOM}
              aria-label="Zoom in"
              title="Zoom in"
            >
              +
            </button>
          </div>
        </div>
        {onSplitSubtitle && isSplitMode && (
          <div className="split-mode-hint">
            {selectedIndex !== null
              ? 'Tap on the selected region to split'
              : 'Select a subtitle first, then tap to split'}
          </div>
        )}
      </div>
    );
  }
);

WaveformPlayer.displayName = 'WaveformPlayer';

export default WaveformPlayer;
